import React, { useEffect, useState } from "react";
import { X } from "lucide-react";

const STORAGE_KEY = "am_cookie_consent_v1";

/**
 * Subtle bottom-left cookie / tracking notice. Black-and-gold,
 * dismissible, persistent. Acknowledges essential local-storage usage
 * and links to the full Privacy Policy. We don't run third-party
 * advertising trackers, so this is informational consent only.
 */
const CookieNotice = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Small delay so it doesn't compete with the hero entrance.
        const t = setTimeout(() => setVisible(true), 1800);
        return () => clearTimeout(t);
      }
    } catch {
      /* localStorage disabled — leave hidden */
    }
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ accepted: true, ts: new Date().toISOString() })
      );
    } catch {
      /* non-fatal */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-notice"
      role="dialog"
      aria-label="Cookie and tracking notice"
      className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-sm z-[60] glass-card p-5 border-gold/30"
    >
      <button
        type="button"
        aria-label="Dismiss notice"
        onClick={dismiss}
        data-testid="cookie-notice-close"
        className="absolute top-3 right-3 text-themed-muted/70 hover:text-gold transition-colors"
      >
        <X size={14} strokeWidth={1.6} />
      </button>
      <p className="text-[10px] tracking-[0.3em] uppercase text-gold/85 mb-2">
        Essential Cookies
      </p>
      <p className="text-themed-muted text-sm leading-relaxed pr-6">
        We use essential local storage to remember your session and personalize
        what you see. No cross-site advertising trackers.{" "}
        <a
          href="/privacy"
          className="text-gold hover:underline"
          data-testid="cookie-notice-privacy-link"
        >
          Read the policy
        </a>
        .
      </p>
      <button
        type="button"
        onClick={dismiss}
        data-testid="cookie-notice-accept"
        className="btn-gold mt-4 text-[11px] py-2 px-5"
      >
        Got It
      </button>
    </div>
  );
};

export default CookieNotice;
